import produce from 'immer';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import StyledFiltersDisplay from 'automation-ui-enrollment/components/common/StyledFiltersDisplay';
import { InboundDbListSegStrategy } from 'customer-data-filters-ui/public/strategies';
import { SegmentationFiltersSharedProvider } from 'customer-data-filters-ui/public/subcomponents';
import ErrorBoundary from 'automation-ui-core/errors/ErrorBoundary';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import { populateTrackingDataFromILSFilters } from 'automation-ui-enrollment/utils/trackingUtils';
import { StyledCriteraBorder } from 'automation-ui-enrollment/components/eventEnrollment/steps/EventEnrollmentReview';
import ReenrollmentSelect from 'automation-ui-enrollment/components/ReenrollmentSelect';
import { WORKFLOWS_APP } from 'automation-ui-source-apps/constants/SourceApps';
import { Section } from 'foundations-components/transitional/components/Section';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { ExclamationIcon, WarningIcon } from 'foundations-assets';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ReenrollmentConfig = ({
  objectTypeId,
  reenrollmentState,
  onReenrollmentStateChange,
  onShouldReenrollChange,
  readOnly,
  shouldReenroll,
  numOrphanedTriggers,
  sourceApp
}) => {
  const trackEvent = useTrackEvent();
  const onToggleItem = index => {
    const reenrollmentStateItem = reenrollmentState[index];
    const newChecked = !reenrollmentStateItem.checked;
    const properties = [];
    const operators = [];
    populateTrackingDataFromILSFilters(reenrollmentStateItem.filterBranch, undefined, properties, operators);
    trackEvent(Trackables.TOGGLE_REENROLLMENT_FILTER, {
      enabled: newChecked,
      objectTypeId,
      property: properties[0],
      operation: operators[0]
    });
    onReenrollmentStateChange(produce(reenrollmentState, draftReenrollmentState => {
      draftReenrollmentState[index].checked = newChecked;
    }));
  };
  const checkboxesDisabled = !shouldReenroll || readOnly;
  const {
    reenrollmentCheckboxesInfo,
    unavailableTriggers,
    hasInvalidReenrollmentTriggers
  } = reenrollmentState.reduce((acc, reenrollmentStateItem, index) => {
    if (reenrollmentStateItem.reEnrollmentTrigger.type === 'NOT_SUPPORTED' && reenrollmentStateItem.checked) {
      // aka "phantom re-enrollment triggers," should be deleted on save
      acc.hasInvalidReenrollmentTriggers = true;
    } else if (reenrollmentStateItem.reEnrollmentTrigger.type === 'NOT_SUPPORTED') {
      acc.unavailableTriggers.push(reenrollmentStateItem.filterBranch);
    } else {
      acc.reenrollmentCheckboxesInfo.push({
        originalIndex: index,
        filterBranch: reenrollmentStateItem.filterBranch,
        checked: reenrollmentStateItem.checked
      });
    }
    return acc;
  }, {
    reenrollmentCheckboxesInfo: [],
    unavailableTriggers: [],
    hasInvalidReenrollmentTriggers: false
  });
  const strategy = InboundDbListSegStrategy.configure({
    isSameObjectAssociationFilteringEnabled: true
  });
  return /*#__PURE__*/_jsxs(Section, {
    "data-test-id": "test",
    children: [/*#__PURE__*/_jsx(ReenrollmentSelect, {
      shouldReenroll: !!shouldReenroll,
      objectTypeId: objectTypeId,
      readOnly: readOnly,
      setShouldReenroll: onShouldReenrollChange,
      reenrollDescription: /*#__PURE__*/_jsxs(List, {
        styled: true,
        paddingInlineStart: "500",
        children: [/*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsx("span", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation-ui-enrollment.redesign.review.manualReenrollment"
            })
          })
        }), !!reenrollmentCheckboxesInfo.length && /*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsx("span", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "enrollment.config.reenrollment.reenrollCriteriaDescription"
            })
          })
        })]
      })
    }), /*#__PURE__*/_jsxs(Section, {
      children: [/*#__PURE__*/_jsxs(SegmentationFiltersSharedProvider, {
        strategy: strategy,
        children: [shouldReenroll && !!reenrollmentCheckboxesInfo.length && /*#__PURE__*/_jsx(StyledCriteraBorder, {
          children: /*#__PURE__*/_jsx(List, {
            children: /*#__PURE__*/_jsx("div", {
              children: reenrollmentCheckboxesInfo.map(({
                originalIndex,
                filterBranch,
                checked
              }, index) => {
                return /*#__PURE__*/_jsx(Flex, {
                  alignItems: "center",
                  children: /*#__PURE__*/_jsx(ErrorBoundary, {
                    boundaryName: "List re-enrollment checkbox",
                    renderError: () => /*#__PURE__*/_jsxs(Checkbox, {
                      disabled: true,
                      readOnly: true,
                      size: "small",
                      onChange: () => {},
                      children: [/*#__PURE__*/_jsx(ExclamationIcon, {
                        size: "600"
                      }), /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "automation-ui-enrollment.filters.singleErrorBoundary.description"
                      })]
                    }, index),
                    children: /*#__PURE__*/_jsx(Checkbox, {
                      "data-test-id": `reenrollment-filter-${index}`,
                      checked: shouldReenroll && checked
                      // `disabled` and `readOnly` change the UI in different ways for Checkbox
                      // set both to match other filters
                      ,
                      disabled: checkboxesDisabled,
                      readOnly: checkboxesDisabled,
                      onChange: () => onToggleItem(originalIndex),
                      size: "small",
                      children: /*#__PURE__*/_jsx(StyledFiltersDisplay, {
                        baseFilterFamily: objectTypeId,
                        value: {
                          filterBranches: [filterBranch],
                          filterBranchOperator: 'OR',
                          filterBranchType: 'OR',
                          filters: []
                        }
                      }, index)
                    }, index)
                  })
                }, index);
              })
            })
          })
        }), shouldReenroll && unavailableTriggers.length > 0 && /*#__PURE__*/_jsxs(Alert, {
          marginBlockStart: "300",
          type: "warning",
          children: [/*#__PURE__*/_jsx("b", {
            children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
              message: "enrollment.config.reenrollment.criteriaAreNotReenrollmentOptionsWarning"
            })
          }), /*#__PURE__*/_jsx(View, {
            marginBlockStart: "400",
            marginBlockEnd: "200",
            children: /*#__PURE__*/_jsx(ErrorBoundary, {
              boundaryName: "Filters unavailable for re-enrollment",
              renderError: () => /*#__PURE__*/_jsxs(_Fragment, {
                children: [/*#__PURE__*/_jsx(ExclamationIcon, {
                  size: "600"
                }), /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "automation-ui-enrollment.filters.singleErrorBoundary.description"
                })]
              }),
              children: /*#__PURE__*/_jsx(StyledFiltersDisplay, {
                baseFilterFamily: objectTypeId,
                value: {
                  filterBranches: unavailableTriggers,
                  filterBranchOperator: 'OR',
                  filterBranchType: 'OR',
                  filters: []
                }
              })
            })
          })]
        })]
      }), shouldReenroll && (hasInvalidReenrollmentTriggers || numOrphanedTriggers > 0) && sourceApp === WORKFLOWS_APP && /*#__PURE__*/_jsxs(Text, {
        as: "small",
        use: "error",
        children: [/*#__PURE__*/_jsx(View, {
          paddingInlineEnd: "200",
          children: /*#__PURE__*/_jsx(WarningIcon, {})
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "enrollment.config.reenrollment.error.invalidReenrollmentTriggersWarning"
        })]
      })]
    })]
  });
};
export default ReenrollmentConfig;